import Image from 'next/image';
import Compare from '../Compare/Compare';
import { useLocale } from '@components/Contexts/LocaleContext';
import { translate2 as translate } from 'utils/translate';
import { useTailwindBreakpoint } from '@components/Contexts/TailwindBreakpointContext';
import { useEffect, useState } from 'react';

const CompareSection = () => {
  const { lang } = useLocale();
  const { screenWidth } = useTailwindBreakpoint();
  const [initWidth, setInitWidth] = useState(null);

  useEffect(() => {
    setInitWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    if (screenWidth) {
      setInitWidth(null);
    }
  }, [screenWidth]);

  return (
    <section id="compare" className="relative w-full">
      <h1 className="Container relative text-h5MobileBold lg:text-h5Bold gradient-head text-center py-8">
        e-Signature vs. Digital Signature
      </h1>

      {/* signature types */}
      <div
        className={
          initWidth >= 1024 || screenWidth >= 1024
            ? 'Container'
            : 'px-4 md:px-12'
        }
      >
        <div className="relative flex flex-col lg:flex-row justify-center">
          <Compare
            header={translate(
              'ลายมือชื่ออิเล็กทรอนิกส์ทั่วไป',
              'Electronic signature',
              lang,
            )}
            section={translate('มาตรา 9', 'Section 9', lang)}
            desc={translate(
              <>
                เป็นลายมือชื่อในรูปแบบใดๆ ไม่ว่าจะเป็นอักษร, อักขระตัวเลข, เสียง
                หรือสัญลักษณ์อื่นใดที่สร้างขึ้นให้อยู่ในรูปแบบอิเล็กทรอนิกส์
                ที่มีลักษณะตามที่กำหนดใน{' '}
                <span className="text-p3Semibold text-[#0D375B]">มาตรา 9</span>{' '}
                แห่งกฎหมายว่าด้วยธุรกรรมทางอิเล็กทรอนิกส์
              </>,
              <>
                A digital signature that meets the characteristics specified in{' '}
                <span className="text-p3Semibold text-[#0D375B]">
                  Section 26
                </span>{' '}
                and is backed by a certificate issued by a Certification
                Authority (CA) supports electronic signatures as outlined in
                Section 28 of the Electronic Transactions Act. This ensures the
                ability to verify document modifications with the Certification
                Authority serving as a trusted intermediary.
              </>,
              lang,
            )}
            src={
              <Image
                src="images/e-signature/compare1.webp"
                alt="ลายมือชื่ออิเล็กทรอนิกส์ทั่วไป"
                width={334}
                height={200}
              />
            }
            tagColor="#E84143"
          />

          <Compare
            header={translate(
              'ลายมือชื่ออิเล็กทรอนิกส์ที่เชื่อถือได้',
              'Digital Signature',
              lang,
            )}
            section={translate('มาตรา 26', 'Section 26', lang)}
            desc={translate(
              <>
                เป็นลายมือชื่ออิเล็กทรอนิกส์ที่มีลักษณะตามที่กำหนดใน{' '}
                <span className="text-p3Semibold text-[#0D375B]">มาตรา 26</span>{' '}
                แห่งกฎหมายว่าด้วยธุรกรรมทางอิเล็กทรอนิกส์
                ที่ได้จากกระบวนการเข้ารหัสลับ (Encrypt)
                ช่วยให้สามารถยืนยันตัวตนเจ้าของลายมือชื่อและตรวจพบการเปลี่ยนแปลงของข้อความและลายมือชื่อได้
                ทำให้ไม่สามารถปฏิเสธความรับผิดชอบจากข้อความที่ได้แสดงเจตนาในการลงลายมือชื่อได้
              </>,
              <>
                A digital signature complies with the characteristics specified
                in{' '}
                <span className="text-p3Semibold text-[#0D375B]">
                  Section 26
                </span>{' '}
                of the Electronic Transactions Act. It is generated through data
                encryption, ensuring the authentication of the signature&apos;s
                owner and detecting any alterations to the document. As a
                result, it prevents the signer from denying responsibility for
                the content of the signed document.
              </>,
              lang,
            )}
            src={
              <Image
                src="images/e-signature/compare2.webp"
                alt="ลายมือชื่ออิเล็กทรอนิกส์ที่เชื่อถือได้"
                width={403}
                height={200}
              />
            }
            tagColor="#29ADCE"
            flexRowReverse={true}
          />

          <Compare
            header={translate(
              'ลายมือชื่ออิเล็กทรอนิกส์ที่เชื่อถือได้',
              'Digital Signature',
              lang,
            )}
            expand={translate(
              'ซึ่งใช้ใบรับรองโดยผู้ให้บริการออกใบรับรอง',
              'which uses a certificate provided by a certificate authority',
              lang,
            )}
            section={translate('มาตรา 28', 'Section 28', lang)}
            desc={translate(
              <>
                เป็นลายมือชื่ออิเล็กทรอนิกส์ที่มีลักษณะตามที่กำหนดใน{' '}
                <span className="text-p3Semibold text-[#0D375B]">มาตรา 26</span>{' '}
                และอาศัยใบรับรองที่ออกโดยผู้ให้บริการออกใบรับรองเพื่อสนับสนุนลายมือชื่ออิเล็กทรอนิกส์ตามที่กำหนดใน{' '}
                <span className="text-p3Semibold text-[#0D375B]">มาตรา 28</span>{' '}
                แห่งกฎหมายว่าด้วยธุรกรรมทางอิเล็กทรอนิกส์
                ดังนั้นจึงสามารถตรวจสอบความเปลี่ยนแปลงนี้ได้โดยมีผู้ให้บริการออกใบรับรอง
                (Certificatation Authority : CA) เป็นตัวกลาง
              </>,
              <>
                A digital signature that meets the characteristics specified in
                <span className="text-p3Semibold text-[#0D375B]">
                  Section 26
                </span>{' '}
                and is backed by a certificate issued by a Certification
                Authority (CA) supports electronic signatures as outlined in{' '}
                <span className="text-p3Semibold text-[#0D375B]">
                  Section 28
                </span>{' '}
                of the Electronic Transactions Act. This ensures the ability to
                verify document modifications with the Certification Authority
                serving as a trusted intermediary.
              </>,
              lang,
            )}
            src={
              <Image
                src={
                  lang === 'en'
                    ? 'images/e-signature/compare3-eng.webp'
                    : 'images/e-signature/compare3.webp'
                }
                alt="ลายมือชื่ออิเล็กทรอนิกส์ที่เชื่อถือได้"
                width={325.82}
                height={200}
              />
            }
            tagColor="#0D375B"
          />
        </div>
      </div>
    </section>
  );
};

export default CompareSection;
